/** @jsxImportSource @emotion/react */

import React, { useState } from 'react';
import styled from 'styled-components';
import Scroll from '@component/common/scroll';
import * as size from '@constant/size';
import { isMobile } from 'react-device-detect';
import { IS_IYAGIGO_MOBILE_VIEW, SERVICE } from '@config/environment';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import TextInput from '@component/common/input/text-input';
import * as design from '@constant/design';
import TextAreaInput from '@component/common/input/text-area-input';
import { useCreateContactMutation } from '@hook/hub/useApi';
import { createErrorText, getIsValidEmail } from '@util/string';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useTypedDispatch } from '@hook/common/useStore';

const Wrapper = styled.div`
  position: relavtive;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING};
`;

export const EMAIL_INPUT_DESIGN = {
  text: { size: 'medium' },
  box: {
    width: IS_IYAGIGO_MOBILE_VIEW ? '100%' : '300px',
    height: size.INPUT_BLOCK_HEIGHT,
    borderWidth: '1px',
    borderColor: color.GRAY_1,
    borderDirections: ['bottom'],
    alignX: 'left',
    padding: `0 ${size.MARGIN_1} 0 ${size.MARGIN_1}`,
  },
} as const;

export const SUBJECT_INPUT_DESIGN = {
  text: { size: 'medium' },
  box: {
    width: '100%',
    height: size.INPUT_BLOCK_HEIGHT,
    borderWidth: '1px',
    borderColor: color.GRAY_1,
    borderDirections: ['bottom'],
    alignX: 'left',
    padding: `0 ${size.MARGIN_1} 0 ${size.MARGIN_1}`,
  },
} as const;

export const CONTENT_INPUT_DESIGN = {
  text: { size: 'medium' },
  box: {
    width: '100%',
    // height: size.AUTH_BLOCK_HEIGHT,
    borderWidth: '1px',
    borderColor: color.GRAY_1,
    alignX: 'left',
    padding: `${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1}`,
  },
} as const;

const NOTIFICATION_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: {
    alignX: 'left',
    padding: size.MARGIN_1,
    width: IS_IYAGIGO_MOBILE_VIEW ? '100%' : 'fit-content',
    borderRadius: size.BORDER_RADIUS_2,
    backgroundColor: color.BROWN_4,
  },
} as const;

const SUBMIT_BUTTON_DESIGN = {
  text: { size: 'medium', color: color.WHITE_1 },
  box: {
    width: '80px',
    height: size.BUTTON_BLOCK_HEIGHT,
    borderRadius: size.BORDER_RADIUS_2,
    event: { cursor: 'pointer' },
    // backgroundColor: color.BROWN_1,
    backgroundColor: color.BLACK_2,
  },
} as const;

const Contact = () => {
  const [email, setEmail] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [errorText, setErrorText] = useState<string>('');
  const createContactMutation = useCreateContactMutation();
  const dispatch = useTypedDispatch();

  const handleEmailChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setErrorText('');
    setEmail(ev.target.value.trim());
  };

  const handleSubjectChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setErrorText('');
    setSubject(ev.target.value.trim());
  };

  const handleContentChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    setErrorText('');
    setContent(ev.target.value.trim());
  };

  const handleSubmitButtonClick = async () => {
    if (!email) return setErrorText('이메일을 기입해주세요.');
    if (!getIsValidEmail(email)) return setErrorText('이메일을 형식을 지켜주세요.');
    if (!subject) return setErrorText('제목을 기입해주세요.');
    if (!content) return setErrorText('내용을 기입해주세요.');

    await createContactMutation.mutateAsync({ service: SERVICE, email, subject, content });
    dispatch(modalActions.showDialogModal({ type: 'plain-text', data: '✅ 제출되었습니다.' }));
    setEmail('');
    setSubject('');
    setContent('');
  };

  return (
    <Wrapper>
      <Scroll direction='y' height={isMobile ? '100%' : '100%'}>
        <TextBlock
          text={
            IS_IYAGIGO_MOBILE_VIEW
              ? '이야기고에 대한 문의나, <br/>함께 만들어갈 아이디어가 있다면 들려주세요. 🙂'
              : '이야기고에 대한 문의나, 함께 만들어갈 아이디어가 있다면 들려주세요. 🙂'
          }
          design={NOTIFICATION_TEXT_DESIGN}
        />
        <Gap height={size.MARGIN_5} />
        <TextInput
          name={'email'}
          placeholder={'이메일'}
          design={EMAIL_INPUT_DESIGN}
          onChange={handleEmailChange}
          value={email}
        />
        <Gap height={size.MARGIN_2} />
        <TextInput
          name={'subject'}
          placeholder={'제목'}
          design={SUBJECT_INPUT_DESIGN}
          onChange={handleSubjectChange}
          value={subject}
        />
        <Gap height={size.MARGIN_2} />
        <TextAreaInput
          name={'content'}
          row={10}
          placeholder={'의견을 적어주세요.'}
          design={CONTENT_INPUT_DESIGN}
          onChange={handleContentChange}
          value={content}
        />
        <TextBlock
          text={errorText}
          design={{
            ...design.ERROR_TEXT_DESIGN,
            box: { ...design.ERROR_TEXT_DESIGN, height: '20px', width: 'fit-content' },
          }}
        />
        <TextBlock text={'제출하기'} design={SUBMIT_BUTTON_DESIGN} click={{ handler: handleSubmitButtonClick }} />
      </Scroll>
    </Wrapper>
  );
};

export default Contact;
